import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { makeStyles, Box, Tabs, Tab } from '@material-ui/core';
import { useAlert } from '../../../common/hooks';
import { AlertSeverity } from '../../../common/providers';
import { EmptyState, Panel, TabPanel } from '../../../common/components';
import { DeviceService } from '../services/DeviceService';
import DeviceList from './partials/DeviceList';
import { StreamPage } from '../../streams';

const TabIndex = {
  Devices: 0,
  AktiveStreams: 1,
};

const useStyles = makeStyles(theme => ({
  container: {
    height: '100%',
    minHeight: '500px',
  },
  content: {
    flex: '1',
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
}));

function DevicesPage() {
  const [t] = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const { showAlert } = useAlert();
  const classes = useStyles();
  const [tabIndex, setTabIndex] = useState(TabIndex.Devices);

  const { data: devices } = useQuery(['devices', userId], async () => {
    const { data } = await DeviceService.fetchDevices(userId);
    return data?.devices;
  });

  const queryClient = useQueryClient();
  const deleteDevices = useMutation(
    async ({ userId, deviceIds }: { userId: string; deviceIds: Array<string> }) => {
      return DeviceService.deleteDevices(userId, deviceIds);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['devices', userId]);
        showAlert({
          title: t('common.success'),
          message: t('device.devicesDeleteSuccess'),
          severity: AlertSeverity.Success,
        });
      },
      onError: (e: Error) => {
        showAlert({
          title: t('device.devicesDeleteError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      },
    }
  );

  const handleDeleteDevices = useCallback(
    async (deviceIds: Array<string>) => {
      await deleteDevices.mutate({
        userId,
        deviceIds,
      });
    },
    [deleteDevices, userId]
  );

  return (
    <Box id="Devices-container" className={classes.container}>
      <Panel id="Devices-panel" variant="contained">
        {!devices ? (
          <EmptyState variant="unavailable" message={t('device.devicesNotAvailable')} />
        ) : (
          <>
            <Tabs
              id="Devices-tabs"
              className={classes.tabs}
              data-value={tabIndex}
              value={tabIndex}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              onChange={(_event, index) => setTabIndex(index)}
            >
              <Tab
                id="Devices-devicesTab"
                label={t('customer.customerDetailsDevices')}
                data-index={TabIndex.Devices}
              />
              <Tab
                id="Devices-streamsTab"
                label={t('customer.customerDetailsStreams')}
                data-index={TabIndex.AktiveStreams}
              />
            </Tabs>
            <Box id="Devices-tabsContent" className={classes.content}>
              <TabPanel index={TabIndex.Devices} currentIndex={tabIndex}>
                <DeviceList
                  data={devices}
                  onDeleteDevices={handleDeleteDevices}
                  actionsDisabled={deleteDevices.isLoading}
                />
              </TabPanel>
              <TabPanel index={TabIndex.AktiveStreams} currentIndex={tabIndex}>
                <StreamPage />
              </TabPanel>
            </Box>
          </>
        )}
      </Panel>
    </Box>
  );
}

export default DevicesPage;
